import React, { useEffect, useState } from "react";
import { Form, Input, Row, Col, DatePicker, Select, Checkbox, TimePicker,Tooltip } from "antd";
import type { CheckboxValueType } from "antd/es/checkbox/Group";
import { WhiteContainer } from "../../styled/Container.styled";
import { IOffer, IOfferByIdResponse } from "../../../types/offers";
import { useGetAllOffersQuery } from "../../../feature/Offer/offerApiSlice";
import dayjs from "dayjs";

type OfferDetailsProps = {
  data?: IOfferByIdResponse;
  setImgFile?: any;
  formRef?: any;
};

const OffersDetailsForm = (props: OfferDetailsProps) => {
  const { data, setImgFile, formRef } = props;
  const [selectedTripTypes, setSelectedTripTypes] = useState<CheckboxValueType[]>([]);
  const [form] = Form.useForm();
  const { data: offersData } = useGetAllOffersQuery();

  // When adding a new offer, its code / name can't match an offer that is still live.
  const isLiveOffer = (offer: IOffer) => {
    // Expired offers and offers the admin switched off are inactive, so their code and name are free.
    if (offer.offerStatus === "EXPIRED" || offer.offerStatus === "INACTIVE") return false;
    // Expiry date + expiry time (end of that day when there is no time).
    const day = dayjs(offer.expireDate).format("YYYY-MM-DD");
    const expiry = offer.expireTime
      ? dayjs(`${day} ${offer.expireTime}`)
      : dayjs(day).endOf("day");
    return expiry.isAfter(dayjs());
  };

  const validateUnique = (field: "couponCode" | "couponName", label: string) => (_: any, value: string) => {
    const entered = (value || "").trim().toLowerCase();
    if (data || !entered) return Promise.resolve();
    const exists = (offersData?.data.content || []).some(
      (offer) => isLiveOffer(offer) && (offer[field] || "").trim().toLowerCase() === entered
    );
    return exists
      ? Promise.reject(new Error(`${label} '${value.trim()}' already exists.`))
      : Promise.resolve();
  };

  const [offerImg, setOfferImg] = useState({
    image_file: null,
    image_preview: " ",
  });

  const [stateDisabled, setStateDisabled] = useState<boolean>(false);

  const imgFileHandler = (e: any) => {
    let image_as_base64 = URL.createObjectURL(e.target.files[0]);
    let image_as_files = e.target.files[0];

    setOfferImg({
      image_preview: image_as_base64,
      image_file: image_as_files,
    });
    setImgFile(image_as_files.name);
  };

  useEffect(() => {
    setOfferImg({
      image_file: null,
      image_preview: `${data?.data.image} `,
    });

    setStateDisabled(data ? true : false);
    
    // Initialize selected trip types from data
    if (data?.data.offerTripTypes) {
      if (Array.isArray(data.data.offerTripTypes)) {
        // API now returns array, use directly
        // An offer saved for every trip type is shown as ALL.
        const types = data.data.offerTripTypes;
        if (types.includes('ALL') || ['LOCAL', 'OUTSTATION', 'RENTAL'].every((t) => types.includes(t))) {
          setSelectedTripTypes(['ALL']);
        } else {
          setSelectedTripTypes(data.data.offerTripTypes);
        }
      } else {
        // Handle legacy string format
        if (data.data.offerTripTypes === 'ALL') {
          setSelectedTripTypes(['ALL']);
        } else {
          setSelectedTripTypes((data.data.offerTripTypes as string).split(','));
        }
      }
    }
  }, [data?.data.image, data?.data.offerTripTypes]);

  const handleTripTypeChange = (checkedValues: CheckboxValueType[]) => {
    const allIndex = checkedValues.indexOf('ALL');
    const hasAll = selectedTripTypes.includes('ALL');
    
    if (allIndex !== -1 && !hasAll) {
      // 'ALL' was just checked
      setSelectedTripTypes(['ALL']);
    } else if (hasAll && checkedValues.length > 1) {
      // 'ALL' was previously selected and other options are being selected
      setSelectedTripTypes(checkedValues.filter(v => v !== 'ALL'));
    } else if (allIndex === -1 && hasAll && checkedValues.length === 0) {
      // 'ALL' was unchecked
      setSelectedTripTypes([]);
    } else {
      // Normal selection
      setSelectedTripTypes(checkedValues);
    }
  };

  // Sync form with selectedTripTypes
  useEffect(() => {
    form.setFieldValue('offerTripTypes', selectedTripTypes);
  }, [selectedTripTypes, form]);

  return (
    <>
      <Form
        layout="vertical"
        form={form}
        ref={formRef}
        name="offerDetailsForm"
        initialValues={{
          couponName: data?.data.couponName,
          couponCode: data?.data.couponCode?.toUpperCase(),
          couponValue: data?.data.couponValue,
          liveDate: dayjs(data?.data.liveDate),
          liveTime: data?.data.liveTime ? dayjs(data?.data.liveTime, 'HH:mm:ss') : null,
          expireDate: dayjs(data?.data.expireDate),
          expireTime: data?.data.expireTime ? dayjs(data?.data.expireTime, 'HH:mm:ss') : null,
          state: data?.data.state,
          image: data?.data.image,
          offerUserType: data?.data.offerUserType || 'EXISTING_USER', // Fallback default value
          offerTripTypes: Array.isArray(data?.data.offerTripTypes) 
            ? data?.data.offerTripTypes || []
            : (data?.data.offerTripTypes ? (data.data.offerTripTypes as string).split(',') : []),
          minimumValue: data?.data.minimumValue,
        }}
      >
        <WhiteContainer>
          <Row gutter={[32, 22]}>
            <Col span={12}>
              <label>Coupon Name</label>
              <Form.Item
                name="couponName"
                rules={[
                  { required: true, message: "Coupon Name is required" },
                  { validator: validateUnique("couponName", "Coupon name") },
                ]}
              >
                <Input />
              </Form.Item>

              <label>Coupon Code</label>
              <Form.Item
                name="couponCode"
                normalize={(value: string) => (value || "").replace(/\s/g, "").toUpperCase()}
                rules={[
                  { required: true, message: "Coupon Code is required" },
                  { validator: validateUnique("couponCode", "Coupon code") },
                ]}
              >
                <Input />
              </Form.Item>

              <label>Coupon Value</label>
              <Form.Item name="couponValue" rules={[{ required: true, message: "Coupon Value is required" }]}>
                <Input />
              </Form.Item>

              <label>Coupon Live Date</label>
              <Form.Item name="liveDate" rules={[{ required: true, message: "Coupon live date is required" }]}>
                <DatePicker className="w-100" />
              </Form.Item>

              <label>Coupon Live Time</label>
              <Form.Item name="liveTime" rules={[{ required: true, message: "Coupon live time is required" }]}>
                <TimePicker className="w-100" format="HH:mm:ss" />
              </Form.Item>

             
            </Col>
            <Col span={12}>
              {/* 
              <Form.Item>
                <Col span={24}>
                  <Row>
                    <Col span={12}>
                      <Card>
                        <img
                          src={offerImg.image_preview}
                          alt="offer"
                          style={imageUploaderStyle}
                        />
                      </Card>
                    </Col>
                    <Col span={12}>
                      <input
                        type="file"
                        name="image"
                        className="custom-offerbtn"
                        onChange={imgFileHandler}
                      />
                    </Col>
                  </Row>
                </Col>
              </Form.Item>
              */}
               <label>Coupon Expiry Date</label>
              <Form.Item name="expireDate" rules={[{ required: true, message: "Coupon expiry date is required" }]}>
                <DatePicker className="w-100" />
              </Form.Item>

              <label>Coupon Expiry Time</label>
              <Form.Item name="expireTime" rules={[{ required: true, message: "Coupon expiry time is required" }]}>
                <TimePicker className="w-100" format="HH:mm:ss" />
              </Form.Item>

              <label>Coupon applied to</label>
              <Form.Item name="offerUserType">
                <Select
                  options={[
                    { value: 'ALL', label: 'All' },
                    { value: 'EXISTING_USER', label: 'Existing User' },
                    { value: 'NEW_USER', label: 'New User' },
                  ]}
                />
              </Form.Item>
              
              <label>Minimum Trip Value</label>
              <Form.Item name="minimumValue" rules={[{ required: true, message: "Minimum Trip Value is required" }]}>
                <Input />
              </Form.Item>

              <label>Offer Trip Type</label>
              <Form.Item 
                name="offerTripTypes"
                rules={[{ required: true, message: "Offer Trip Type is required" }]}
              >
                <Checkbox.Group 
                  value={selectedTripTypes}
                  onChange={handleTripTypeChange}
                >
                  <Row>
                    <Col span={6}>
                      <Tooltip title="This offer will be applicable for all trip types">
                        <Checkbox
                          value="ALL"
                          disabled={selectedTripTypes.length > 0 && !selectedTripTypes.includes('ALL')}
                        >
                          ALL
                        </Checkbox>
                      </Tooltip>
                    </Col>
                    <Col span={6}>
                      <Tooltip title="This offer will be applicable only for local trips">
                        <Checkbox
                          value="LOCAL"
                          disabled={selectedTripTypes.includes('ALL')}
                        >
                          LOCAL
                        </Checkbox>
                      </Tooltip>
                    </Col>
                    <Col span={8}>
                      <Tooltip title="This offer will be applicable only for outstation trips">
                        <Checkbox
                          value="OUTSTATION"
                          disabled={selectedTripTypes.includes('ALL')}
                        >
                          OUTSTATION
                        </Checkbox>
                      </Tooltip>
                    </Col>
                    <Col span={6}>
                      <Tooltip title="This offer will be applicable only for rental trips">
                        <Checkbox
                          value="RENTAL"
                          disabled={selectedTripTypes.includes('ALL')}
                        >
                          RENTAL
                        </Checkbox>
                      </Tooltip>
                    </Col>
                  </Row>
                </Checkbox.Group>
              </Form.Item>
            </Col>
          </Row>
        </WhiteContainer>
      </Form>
    </>
  );
};

export default OffersDetailsForm;
